import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/kit";
import { rpc } from "@/lib/supabase";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const field =
  "w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3.5 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-[#FF6A00]";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-400";

export default function ProfilePage() {
  const { profile, role, user, refreshProfile, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate("/auth", { replace: true });
    } catch (err) {
      toast.error(err?.message || "No se pudo cerrar sesión");
      setLoggingOut(false);
    }
  };
  const [f, setF] = useState({
    full_name: profile?.full_name || "",
    phone: profile?.phone || "",
    business_name: profile?.business_name || "",
  });
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await rpc("update_my_profile", {
        p_full_name: f.full_name,
        p_phone: f.phone,
        p_business_name: role === "BUSINESS" ? f.business_name : null,
      });
      if (pw) {
        const { error } = await updatePassword(pw);
        if (error) throw error;
        setPw("");
      }
      await refreshProfile();
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(err?.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Perfil" subtitle="Administra tu información de cuenta." testId="profile-page" />
      <div className="max-w-xl">
        <Card>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className={labelCls}>Correo</label>
              <input className={`${field} opacity-60`} value={user?.email || ""} disabled data-testid="profile-email" />
            </div>
            <div>
              <label className={labelCls}>Nombre completo</label>
              <input className={field} value={f.full_name} onChange={set("full_name")} data-testid="profile-fullname" />
            </div>
            {role === "BUSINESS" && (
              <div>
                <label className={labelCls}>Nombre del negocio</label>
                <input className={field} value={f.business_name} onChange={set("business_name")} data-testid="profile-business" />
              </div>
            )}
            <div>
              <label className={labelCls}>Teléfono</label>
              <input className={field} value={f.phone} onChange={set("phone")} data-testid="profile-phone" />
            </div>
            <div>
              <label className={labelCls}>Nueva contraseña (opcional)</label>
              <input type="password" className={field} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Dejar en blanco para no cambiar" data-testid="profile-password" />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="profile-save"
              className="rounded-xl bg-[#FF6A00] px-6 py-3 font-extrabold text-black transition-transform active:scale-95 disabled:opacity-60"
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </form>
        </Card>
        {role === "DRIVER" && <PayoutAccountCard />}
        <div className="mt-8 border-t border-[#27272A] pt-6">
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              data-testid="profile-logout-button"
              className="flex items-center gap-2 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
            <AlertDialogContent
              className="border-[#27272A] bg-[#141416] text-white"
              data-testid="logout-confirm-dialog"
            >
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">¿Cerrar sesión?</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  Tendrás que volver a iniciar sesión para acceder a tu cuenta.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid="logout-cancel-button"
                  className="border-[#27272A] bg-transparent text-gray-300 hover:bg-[#1c1c1f] hover:text-white"
                >
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleLogout}
                  disabled={loggingOut}
                  data-testid="logout-confirm-button"
                  className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {loggingOut ? "Cerrando…" : "Cerrar sesión"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

const field2 =
  "w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3.5 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-[#FF6A00]";
const labelCls2 = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-400";

function PayoutAccountCard() {
  const [current, setCurrent] = useState(undefined); // undefined=cargando, null=sin datos
  const [f, setF] = useState({ method: "NEQUI", holder_full_name: "", holder_id_number: "", account_number: "", phone_number: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { rpc("get_my_payout_account").then((d) => setCurrent(d)).catch(() => setCurrent(null)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await rpc("upsert_my_payout_account", {
        p_method: f.method, p_holder_full_name: f.holder_full_name, p_holder_id_number: f.holder_id_number,
        p_account_number: f.method === "BANCOLOMBIA" ? f.account_number : null,
        p_phone_number: f.method === "NEQUI" ? f.phone_number : null,
      });
      const d = await rpc("get_my_payout_account");
      setCurrent(d);
      toast.success("Datos de pago guardados");
    } catch (err) {
      toast.error(err?.message || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  return (
    <Card className="mt-6" data-testid="payout-account-card">
      <h2 className="font-heading text-lg font-bold text-white">Datos de pago</h2>
      <p className="mb-4 mt-1 text-xs text-gray-400">RAPIFAST usa estos datos para liquidarte. No guardamos claves ni PIN. Se muestran enmascarados.</p>
      {current && (
        <div className="mb-4 rounded-xl border border-[#27272A] bg-[#101012] p-3 text-sm text-gray-300" data-testid="payout-current">
          Actual: <b>{current.method}</b> · {current.holder_full_name} · CC {current.holder_id_number}
          {current.method === "BANCOLOMBIA" && ` · ${current.account_number}`}
          {current.method === "NEQUI" && ` · ${current.phone_number}`}
        </div>
      )}
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className={labelCls2}>Método</label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger className={field2} data-testid="payout-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NEQUI">Nequi</SelectItem>
              <SelectItem value="BANCOLOMBIA">Bancolombia</SelectItem>
              <SelectItem value="CASH">Efectivo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls2}>Nombre completo</label>
          <input className={field2} value={f.holder_full_name} onChange={set("holder_full_name")} data-testid="payout-holder" />
        </div>
        <div>
          <label className={labelCls2}>Cédula</label>
          <input className={field2} value={f.holder_id_number} onChange={set("holder_id_number")} data-testid="payout-id" />
        </div>
        {f.method === "BANCOLOMBIA" && (
          <div>
            <label className={labelCls2}>Número de cuenta</label>
            <input className={field2} value={f.account_number} onChange={set("account_number")} data-testid="payout-account-number" />
          </div>
        )}
        {f.method === "NEQUI" && (
          <div>
            <label className={labelCls2}>Celular asociado</label>
            <input className={field2} value={f.phone_number} onChange={set("phone_number")} data-testid="payout-phone" />
          </div>
        )}
        <button type="submit" disabled={saving} data-testid="payout-save"
          className="rounded-xl bg-[#FF6A00] px-6 py-3 font-extrabold text-black transition-transform active:scale-95 disabled:opacity-60">
          {saving ? "Guardando…" : "Guardar datos de pago"}
        </button>
      </form>
    </Card>
  );
}
